// ExtratoCredito — trajetória completa de um crédito da loja.
// Usado no HistoricoTrocas (expansão + modal) e em CreditosLista (modal de detalhe).
// Os rótulos e cores de tipo são idênticos em ambos os contextos (DRY).
import { Fragment } from 'react';
import { formatMoeda, formatDataHoraSP } from '@fiodecobre/shared';

export interface ItemVendaExtrato {
  id: number;
  codigo_interno: string | null;
  produto_descricao: string;
  foto_url: string | null;
  quantidade: string;
  preco_unitario: string;
  subtotal: string;
  unidade_sigla: string | null;
}

export interface MovimentacaoExtrato {
  tipo: string; // 'uso' | 'estorno' | 'cancelamento' — e qualquer futuro
  valor: string;
  saldo_antes: string;
  saldo_depois: string;
  venda_numero: string | null;
  // Compra com Credito (migration 0340): uso sem venda — o TRC da compra que gastou o credito.
  compra_numero?: string | null;
  operador_nome: string | null;
  criado_em: string;
  // NF autorizada da venda onde o credito foi aplicado (so em mov de 'uso').
  // modelo: '65' = NFC-e · '55' = NF-e. Pode ser null se a venda nao teve NF.
  nf_numero?: number | null;
  nf_serie?: string | null;
  nf_modelo?: string | null;
  // itens da venda onde o credito foi gasto (so em mov de 'uso')
  itens_venda?: ItemVendaExtrato[];
  // campo legado do CreditosLista (motivo/observacao)
  motivo?: string | null;
}

export interface CreditoExtrato {
  numero: string;
  valor_original: string;
  valor_utilizado: string;
  saldo: string;
  status: 'ativo' | 'consumido' | 'cancelado' | string;
  criado_em: string;
  movimentacoes: MovimentacaoExtrato[];
}

interface Props {
  credito: CreditoExtrato;
  // quando true, compacta o padding (uso na expansão inline da tabela)
  compacto?: boolean;
}

// Mapeia o tipo de movimentação do banco para rótulo e cor de texto.
// Banco grava: 'uso' (consumo em venda), 'estorno' (estorno por cancelamento de venda),
// 'cancelamento' (cancelamento direto do crédito).
// Legado de CreditosLista também envia 'geracao'/'consumo' — tratados aqui para compatibilidade.
function tipoLabel(tipo: string): { label: string; cor: string } {
  switch (tipo) {
    case 'uso':
    case 'consumo':
      return { label: 'Consumido', cor: '#2563eb' };
    case 'estorno':
      return { label: 'Estornado', cor: '#d97706' };
    case 'cancelamento':
      return { label: 'Cancelado', cor: '#dc2626' };
    case 'geracao':
      return { label: 'Gerado', cor: '#16a34a' };
    default:
      return { label: tipo, cor: 'var(--text-muted)' };
  }
}

// Badge de status do crédito — daltonismo-safe: distinção por texto, não só cor.
// Ativo: fundo amarelo #fde68a + texto preto (padrão de destaque positivo da casa).
// Parcial / Consumido: tom neutro azul (informacional, sem conotação bom/ruim).
// Cancelado: neutro cinza.
function statusBadgeCredito(status: string) {
  if (status === 'ativo') {
    return (
      <span style={{
        fontSize: 11, padding: '2px 8px', background: '#fde68a', color: '#000',
        borderRadius: 4, fontWeight: 700,
      }}>Ativo</span>
    );
  }
  if (status === 'consumido') {
    return (
      <span style={{
        fontSize: 11, padding: '2px 8px', background: '#dbeafe', color: '#1e40af',
        borderRadius: 4, fontWeight: 600,
      }}>Consumido</span>
    );
  }
  return (
    <span style={{
      fontSize: 11, padding: '2px 8px', background: 'var(--grid-row-alt-bg)', color: 'var(--text-muted)',
      borderRadius: 4, fontWeight: 600, border: '1px solid var(--border)',
    }}>Cancelado</span>
  );
}

// Linha de saldo remanescente no rodapé do extrato.
function rodapeSaldo(credito: CreditoExtrato) {
  const saldo = Number(credito.saldo);
  const original = Number(credito.valor_original);
  const utilizado = Number(credito.valor_utilizado);

  if (credito.status === 'cancelado') {
    return { texto: `Saldo restante: ${formatMoeda(saldo)} · cancelado`, destaque: false };
  }
  if (saldo <= 0.001 || credito.status === 'consumido') {
    return { texto: `Saldo restante: ${formatMoeda(0)} · totalmente usado`, destaque: false };
  }
  if (utilizado > 0.001) {
    return { texto: `Saldo restante: ${formatMoeda(saldo)} · parcialmente usado (${formatMoeda(utilizado)} de ${formatMoeda(original)})`, destaque: true };
  }
  return { texto: `Saldo restante: ${formatMoeda(saldo)} · disponível`, destaque: true };
}

export function ExtratoCredito({ credito, compacto }: Props) {
  const pad = compacto ? 8 : 12;
  const rodape = rodapeSaldo(credito);

  return (
    <div style={{
      background: 'var(--card-bg)', border: '1px solid var(--border)',
      borderRadius: 6, padding: pad,
    }}>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>Crédito gerado: {credito.numero}</span>
        {statusBadgeCredito(credito.status)}
      </div>

      {/* Resumo em linha */}
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
        Original <strong style={{ color: 'var(--text-color, inherit)' }}>{formatMoeda(credito.valor_original)}</strong>
        {' · '}Utilizado <strong style={{ color: 'var(--text-color, inherit)' }}>{formatMoeda(credito.valor_utilizado)}</strong>
        {' · '}Saldo <strong style={{ color: 'var(--text-color, inherit)' }}>{formatMoeda(credito.saldo)}</strong>
      </div>

      {/* Extrato de movimentações — linha sintética de geração sempre no topo */}
      {(() => {
        // Filtra qualquer linha real que por acaso viesse com tipo 'geracao'/'gerado'
        // (o banco não grava esse tipo, mas garante não duplicar).
        const movsReais = credito.movimentacoes.filter(
          (m) => m.tipo !== 'geracao' && m.tipo !== 'gerado',
        );

        // Linha sintética de geração: sempre presente, representa a origem do saldo.
        const linhaGeracao: MovimentacaoExtrato & { _sintetica?: true } = {
          _sintetica: true,
          tipo: 'geracao',
          valor: credito.valor_original,
          saldo_antes: '0',
          saldo_depois: credito.valor_original,
          venda_numero: null,
          operador_nome: null,
          criado_em: credito.criado_em,
        };

        const todas = [linhaGeracao, ...movsReais];

        return (
          <table className="tabela tabela-aninhada" style={{ width: '100%', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ width: 140 }}>Data</th>
                <th style={{ width: 90 }}>Tipo</th>
                <th style={{ textAlign: 'right', width: 90 }}>Valor</th>
                <th style={{ textAlign: 'right', width: 90 }}>Saldo após</th>
                <th>Detalhe</th>
              </tr>
            </thead>
            <tbody>
              {todas.map((m, idx) => {
                const { label, cor } = tipoLabel(m.tipo);
                // Sinal: uso/cancelamento = débito (−); estorno/geracao = crédito (+)
                const ehDebito = m.tipo === 'uso' || m.tipo === 'consumo' || m.tipo === 'cancelamento';
                const itensVenda = m.itens_venda ?? [];
                return (
                  <Fragment key={idx}>
                    <tr>
                      <td>{formatDataHoraSP(m.criado_em)}</td>
                      <td><span style={{ color: cor, fontWeight: 600 }}>{label}</span></td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        {ehDebito ? '−' : '+'}{formatMoeda(m.valor)}
                      </td>
                      <td style={{ textAlign: 'right' }}>{formatMoeda(m.saldo_depois)}</td>
                      <td>
                        {m.venda_numero && (
                          <span style={{ marginRight: 6 }}>Venda {m.venda_numero}</span>
                        )}
                        {m.compra_numero && (
                          <span style={{ marginRight: 6 }}>Compra com credito {m.compra_numero}</span>
                        )}
                        {m.nf_numero ? (
                          <span style={{
                            marginRight: 6, padding: '1px 7px', borderRadius: 4,
                            fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                            background: 'var(--grid-row-alt-bg)', border: '1px solid var(--border)',
                          }}>
                            {m.nf_modelo === '55' ? 'NF-e' : 'NFC-e'} nº {m.nf_numero}
                          </span>
                        ) : m.tipo === 'uso' || m.tipo === 'consumo' ? (
                          <span style={{ marginRight: 6, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>sem NF</span>
                        ) : null}
                        {m.operador_nome && (
                          <span style={{ color: 'var(--text-muted)' }}>· {m.operador_nome}</span>
                        )}
                        {m.motivo && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{m.motivo}</div>
                        )}
                      </td>
                    </tr>
                    {itensVenda.length > 0 && (
                      <tr>
                        <td colSpan={5} style={{ padding: '2px 0 10px 32px', borderLeft: '3px solid var(--primary)' }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 5px' }}>
                            Itens comprados nessa venda{m.venda_numero ? ` (${m.venda_numero})` : ''}:
                          </div>
                          <table className="tabela tabela-aninhada" style={{ width: '100%', fontSize: 12 }}>
                            <thead>
                              <tr>
                                <th style={{ width: 36 }}></th>
                                <th style={{ width: 64 }}>Cod</th>
                                <th>Produto</th>
                                <th style={{ textAlign: 'right', width: 70 }}>Qtd</th>
                                <th style={{ textAlign: 'right', width: 84 }}>Unit.</th>
                                <th style={{ textAlign: 'right', width: 94 }}>Subtotal</th>
                              </tr>
                            </thead>
                            <tbody>
                              {itensVenda.map((it) => (
                                <tr key={it.id}>
                                  <td>{it.foto_url ? <img src={it.foto_url} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} /> : null}</td>
                                  <td className="text-gray-500 font-bold no-underline">{it.codigo_interno || '-'}</td>
                                  <td>{it.produto_descricao}</td>
                                  <td style={{ textAlign: 'right' }}>{Number(it.quantidade) % 1 === 0 ? Number(it.quantidade) : Number(it.quantidade).toFixed(3)}{it.unidade_sigla ? ` ${it.unidade_sigla}` : ''}</td>
                                  <td style={{ textAlign: 'right' }}>{formatMoeda(it.preco_unitario)}</td>
                                  <td style={{ textAlign: 'right' }}>{formatMoeda(it.subtotal)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        );
      })()}

      {/* Rodapé com saldo remanescente */}
      <div style={{
        marginTop: 8, padding: '6px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
        background: rodape.destaque ? '#fde68a' : 'var(--grid-row-alt-bg)',
        color: rodape.destaque ? '#000' : 'var(--text-muted)',
        border: `1px solid ${rodape.destaque ? '#f59e0b' : 'var(--border)'}`,
      }}>
        {rodape.texto}
      </div>
    </div>
  );
}
